import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import posthog from 'posthog-js'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '../../lib/supabase'
import { setAuthTokenGetter } from '../../api/client'

interface AuthContextValue {
  session: Session | null
  user: User | null
  isLoaded: boolean
  isSignedIn: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// Emails flagged as internal so PostHog dashboards can exclude founder
// dogfooding from product analytics. Extend if a teammate joins.
const INTERNAL_EMAIL_PATTERNS = [
  /^jonathan\.perbet@/i,
]

function isInternalEmail(email: string | undefined): boolean {
  if (!email) return false
  return INTERNAL_EMAIL_PATTERNS.some((re) => re.test(email))
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('auth')
  const [session, setSession] = useState<Session | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setIsLoaded(true)
    })

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession)
    })

    return () => {
      subscription.subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    setAuthTokenGetter(async () => {
      const { data } = await supabase.auth.getSession()
      return data.session?.access_token ?? null
    })
  }, [])

  // Identify the user to PostHog so error events / analytics are attributed to a
  // stable distinct_id matching the backend's userId. The distinct_id is the
  // Supabase UUID (pseudonymous); we deliberately do NOT send the email, so the
  // analytics vendor never holds a direct identifier (GDPR audit, minimisation).
  // `is_internal` is derived from the email locally but only the boolean is sent,
  // so dashboards can still exclude internal dogfooding.
  useEffect(() => {
    const authUser = session?.user
    if (!authUser) return
    try {
      posthog.identify(authUser.id, {
        tenant_id: authUser.app_metadata?.tenant_id,
        is_internal: isInternalEmail(authUser.email),
      })
    } catch {
      // PostHog disabled / not initialized: silent no-op
    }
  }, [session?.user])

  async function signOut() {
    try {
      posthog.reset()
    } catch {
      // ignore
    }
    await supabase.auth.signOut()
    // Cached data must not outlive the session: on a shared computer the next
    // person would glimpse it before their own loads (audit S-46).
    queryClient.clear()
  }

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 rounded-full border-4 border-muted border-t-primary animate-spin" />
          <p className="text-13 text-muted-foreground">{t('loadingAuth')}</p>
        </div>
      </div>
    )
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        isLoaded,
        isSignedIn: !!session,
        signOut
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
